/**
 * G011 — 生产链与资源经济。
 *
 * 设计取舍见 docs/goals/G011-design-notes.md。决定本文件形状的一条：
 *
 * **生产步骤绝不接触共享库存。** 产线只读写自己的输入/输出缓冲，物流层单独负责
 * 「库存 ⇄ 缓冲」的搬运。顺序无关性因此是**结构性质**，而不是靠排序约定维持的
 * 约定俗成 —— 换个遍历顺序结果必然相同，不需要任何排序键来保证。
 *
 * 单位一律整数（与 G009/G010 同理由：浮点求和对顺序敏感）：
 * 库存与缓冲用「单位」，配方进度用「分钟」，维护欠账用「单位·分钟」。
 */

import { cellKey, type BuildCellId } from '../spatial/types.js';
import { MINUTES_PER_TICK, MINUTES_PER_DAY } from '../kernel.js';
import type { SimulationSystem } from '../system.js';
import type { SimulationContext } from '../context.js';

/* ===================== 建筑投影（依赖注入） ===================== */

/** 一条配方。字段与 game-data 的 `RecipeDefinition` 对齐。 */
export interface ProductionRecipe {
  readonly id: string;
  /** 一个生产周期的时长（游戏内分钟，整数且 > 0）。 */
  readonly cycleMinutes: number;
  readonly inputs: Readonly<Record<string, number>>;
  readonly outputs: Readonly<Record<string, number>>;
  /** 该配方是否需要供水。需氧同理，但氧气目前只用于生命维持，故只留水。 */
  readonly requiresWater?: number;
}

/** 生产所需的最小建筑定义投影。 */
export interface ProductionSpec {
  readonly id: string;
  /** 配方列表。空表示这栋楼不生产（仓库、住舱等）。 */
  readonly recipes: readonly ProductionRecipe[];
  /** 需要配齐的岗位数。0 表示无人值守。 */
  readonly workerSlots: number;
  /** 每天的维护消耗。按 Tick 摊销为整数欠账，满一天结算一单位。 */
  readonly maintenancePerDay: Readonly<Record<string, number>>;
  /** 提供的殖民地仓储容量（单位）。仓库为正，其余为 0。 */
  readonly storageCapacity: number;
  /**
   * 单种资源的缓冲上限（单位）。输入缓冲按此上限拉料，输出缓冲满即停产。
   * 取 0 时回落到 `DEFAULT_BUFFER`，避免内容漏填直接把产线锁死。
   */
  readonly bufferCapacity: number;
  /** 是否需要供电才能生产。 */
  readonly requiresPower: boolean;
}
export type GetProductionSpec = (defId: string) => ProductionSpec | undefined;

/** 内容漏填 bufferCapacity 时的回落值。取一个能装下数个周期的量。 */
export const DEFAULT_BUFFER = 100;

/** 无仓库时的殖民地基础仓储容量（单位/种）。 */
export const BASE_STORAGE = 500;

/* ===================== 停机原因 ===================== */

/**
 * 停机原因是**单一稳定码**，不是一串布尔。
 * 先判公用设施再判物料 —— 断电时「缺料」不是根因，报出来会让玩家去修错的东西。
 */
export type ProductionBlock =
  | 'NO_POWER'
  | 'POWER_SHED'
  | 'NO_WATER'
  | 'NO_WORKERS'
  | 'INPUT_MISSING'
  | 'OUTPUT_FULL';

/* ===================== 世界状态 ===================== */

export interface ProductionLine {
  /** 当前配方 id。 */
  readonly recipeId: string;
  /** 已累计的加工分钟（整数，达到 cycleMinutes 即产出）。 */
  progressMinutes: number;
}

export interface ProductionEntityState {
  /** 输入缓冲：资源 -> 数量。 */
  inputs: Record<string, number>;
  /** 输出缓冲：资源 -> 数量。 */
  outputs: Record<string, number>;
  /** 各配方的进度。 */
  lines: Record<string, number>;
  /** 已认领的岗位数。 */
  workers: number;
  /** 本 Tick 的停机原因；null = 正常运转。 */
  blocked: ProductionBlock | null;
  /** 维护欠账（单位·分钟）。满 MINUTES_PER_DAY 结算一单位。 */
  maintenanceDebt: Record<string, number>;
}

/** 单种资源的来源/消耗分解（HUD 用）。 */
export interface ResourceFlow {
  /** 每游戏日的产出速率（单位/日，整数化到 1/日）。 */
  readonly producedPerDay: number;
  readonly consumedPerDay: number;
  /** 按建筑定义 id 分解的产出与消耗。 */
  readonly byProducer: Readonly<Record<string, number>>;
  readonly byConsumer: Readonly<Record<string, number>>;
}

export interface ProductionState {
  /** 每个生产实体的状态。 */
  entities: Record<string, ProductionEntityState>;
  /** 殖民地可用劳动力总数。G012 落地后改为由人口推导（见设计笔记 §3）。 */
  workforceAvailable: number;
  /** 已被认领的劳动力。 */
  workforceAssigned: number;
  /** 滚动窗口内的流量统计，用于 HUD 趋势。 */
  flows: Record<string, ResourceFlow>;
  /** 统计窗口累计的分钟数（到 MINUTES_PER_DAY 结算一次 flows）。 */
  windowMinutes: number;
  /** 窗口内的产出/消耗累计（单位），按 资源 -> 建筑定义 -> 量。 */
  windowProduced: Record<string, Record<string, number>>;
  windowConsumed: Record<string, Record<string, number>>;
  /** 殖民地仓储容量（单种资源上限）。由仓库数量推导，缓存于此供 UI 读取。 */
  storageCapacity: number;
}

export function createEmptyProductionState(): ProductionState {
  return {
    entities: {}, workforceAvailable: 0, workforceAssigned: 0,
    flows: {}, windowMinutes: 0, windowProduced: {}, windowConsumed: {},
    storageCapacity: BASE_STORAGE,
  };
}

export const PRODUCTION_DOMAIN = 'production';
interface WorldWithProduction { [PRODUCTION_DOMAIN]: ProductionState }
function productionOf(ctx: SimulationContext): ProductionState {
  return (ctx.state as unknown as WorldWithProduction)[PRODUCTION_DOMAIN];
}

/* ===================== 建筑遍历 ===================== */

interface BuildingLike {
  readonly defId: string;
  readonly anchor: BuildCellId;
  readonly lifecycle: string;
}

interface EntitiesLike {
  readonly order: readonly string[];
  readonly components: Record<string, Record<string, unknown>>;
}

/**
 * 人口域的**最小结构投影**（G012）。
 *
 * 这里读世界状态而不是 `import` population 模块 —— 与本文件读 `entities`/`resources`
 * 同一种做法：域之间通过**数据**而非**代码**耦合。production 不需要知道
 * 居民、技能、班次这些概念，只需要「这栋楼此刻有几个人在岗」。
 */
interface StaffingLike {
  readonly order: readonly string[];
  readonly staffedCount: Readonly<Record<string, number>>;
}

/**
 * 处于运转状态的建筑，**按锚点 cellKey 升序**。
 *
 * 排序键用 cellKey 而不是 entityId：cellKey 由位置决定，不含建造先后；
 * entityId 会把插入顺序偷偷编码进结果（G009 报告第 8 节记录过这个教训）。
 *
 * 生产步骤本身与顺序无关（只碰自己的缓冲），这个排序只服务于物流层的
 * 稀缺分配 —— 那里确实要决定「库存不够时谁先拿」。
 */
function operationalSorted(
  entities: EntitiesLike,
  getSpec: GetProductionSpec,
): Array<{ entityId: string; building: BuildingLike; spec: ProductionSpec }> {
  const out: Array<{ entityId: string; building: BuildingLike; spec: ProductionSpec; key: string }> = [];
  for (const entityId of entities.order) {
    const bc = entities.components['building']?.[entityId] as BuildingLike | undefined;
    if (!bc || bc.lifecycle !== 'operational') continue;
    const spec = getSpec(bc.defId);
    if (!spec) continue;
    out.push({ entityId, building: bc, spec, key: cellKey(bc.anchor) });
  }
  out.sort((x, y) => (x.key < y.key ? -1 : x.key > y.key ? 1 : 0));
  return out.map(({ entityId, building, spec }) => ({ entityId, building, spec }));
}

function bufferCap(spec: ProductionSpec): number {
  return spec.bufferCapacity > 0 ? spec.bufferCapacity : DEFAULT_BUFFER;
}

function ensureEntity(prod: ProductionState, entityId: string): ProductionEntityState {
  let e = prod.entities[entityId];
  if (!e) {
    e = { inputs: {}, outputs: {}, lines: {}, workers: 0, blocked: null, maintenanceDebt: {} };
    prod.entities[entityId] = e;
  }
  return e;
}

/* ===================== 公用设施状态（依赖注入） ===================== */

/**
 * 生产需要知道每栋楼的供电/供水状况，但 production 不该 import power/fluid ——
 * 那会把两个域绑死，且让单测必须搭起整个电网才能测一条配方。
 * 改为注入两个查询函数，由 kernel 组装时接上真实实现。
 */
export interface UtilityProbe {
  /** 该实体是否已接入电网。 */
  powered(entityId: string): boolean;
  /** 该实体是否被限电切断。 */
  shed(entityId: string): boolean;
  /** 该实体所在管网是否有净水可用。 */
  hasWater(entityId: string): boolean;
}

/** 全通的探针，供不关心公用设施的单测使用。 */
export const ALWAYS_SUPPLIED: UtilityProbe = {
  powered: () => true, shed: () => false, hasWater: () => true,
};

/* ===================== 系统 ===================== */

export interface ProductionSystemOptions {
  readonly getSpec: GetProductionSpec;
  readonly utility: UtilityProbe;
}

/**
 * 生产系统。一个 Tick 内固定四步，顺序即契约：
 *
 *   1. 容量与用工结算（仓储容量、劳动力认领）
 *   2. 物流：库存 → 输入缓冲，输出缓冲 → 库存
 *   3. 生产：只碰自己的缓冲
 *   4. 维护与统计
 *
 * 物流放在生产**之前**：本 Tick 产出的东西要下一 Tick 才会被搬走，
 * 于是「产出→消耗」跨 Tick 传递，链式复制在时间轴上就不可能发生。
 */
export function createProductionSystem(opts: ProductionSystemOptions): SimulationSystem {
  const { getSpec, utility } = opts;

  return {
    id: 'production',
    update(ctx: SimulationContext): void {
      const prod = productionOf(ctx);
      const entities = ctx.state.entities as unknown as EntitiesLike;
      const stock = ctx.state.resources.stock as Record<string, number>;
      const list = operationalSorted(entities, getSpec);

      /* --- 1. 仓储容量与劳动力 --- */
      let capacity = BASE_STORAGE;
      for (const { spec } of list) capacity += spec.storageCapacity;
      prod.storageCapacity = capacity;

      /* 用工来源二选一：
         - 有居民（G012 已播种）→ 用**逐楼在岗人数**，由人口系统按指派与班次算出；
         - 没有居民 → 回落到 `workforceAvailable` 这个殖民地级人力池。
         回落不是兼容层，而是让「只测经济、不搭人口」的场景仍然成立
         （G011 的 3 日经济场景与全部单测走的就是这条路）。 */
      const staffing = (ctx.state as unknown as { population?: StaffingLike }).population;
      const useCitizens = (staffing?.order.length ?? 0) > 0;

      if (useCitizens) {
        let assigned = 0;
        for (const { entityId, spec } of list) {
          const e = ensureEntity(prod, entityId);
          const onDuty = staffing!.staffedCount[entityId] ?? 0;
          // 仍是「要么配齐要么不配」：半个班组产不出东西
          const got = onDuty >= spec.workerSlots ? spec.workerSlots : 0;
          e.workers = got;
          assigned += got;
        }
        prod.workforceAssigned = assigned;
        prod.workforceAvailable = staffing!.order.length;
      } else {
        let laborLeft = prod.workforceAvailable;
        for (const { entityId, spec } of list) {
          const e = ensureEntity(prod, entityId);
          const want = spec.workerSlots;
          const got = want <= laborLeft ? want : 0;
          e.workers = got;
          laborLeft -= got;
        }
        prod.workforceAssigned = prod.workforceAvailable - laborLeft;
      }

      /* --- 2. 物流：先回收产出，再补充输入 --- */
      // 先推产出：腾出的库存空间可以立刻用于本 Tick 的补料，减少无谓的阻塞。
      for (const { entityId, spec } of list) {
        const e = ensureEntity(prod, entityId);
        for (const res of Object.keys(e.outputs).sort()) {
          const have = e.outputs[res] ?? 0;
          if (have <= 0) continue;
          const room = capacity - (stock[res] ?? 0);
          if (room <= 0) continue;                  // 仓储满 → 留在缓冲里（验收第 3 条）
          const move = have < room ? have : room;
          e.outputs[res] = have - move;
          stock[res] = (stock[res] ?? 0) + move;
        }
        void spec;
      }
      // 再拉输入：库存不足时按 cellKey 升序先到先得，与建造顺序无关。
      for (const { entityId, spec } of list) {
        const e = ensureEntity(prod, entityId);
        const cap = bufferCap(spec);
        for (const recipe of spec.recipes) {
          for (const res of Object.keys(recipe.inputs).sort()) {
            const held = e.inputs[res] ?? 0;
            const want = cap - held;
            if (want <= 0) continue;
            const avail = stock[res] ?? 0;
            if (avail <= 0) continue;
            const move = want < avail ? want : avail;
            e.inputs[res] = held + move;
            stock[res] = avail - move;
          }
        }
      }

      /* --- 3. 生产：只碰自己的缓冲，因此与遍历顺序无关 --- */
      for (const { entityId, building, spec } of list) {
        const e = ensureEntity(prod, entityId);
        if (spec.recipes.length === 0) { e.blocked = null; continue; }

        const block = blockingReason(e, spec, entityId, utility);
        e.blocked = block;
        if (block !== null) continue;

        for (const recipe of spec.recipes) {
          if (!canRun(e, recipe, spec)) continue;
          // 起一个新周期时才扣输入 —— 中途停机不退料，与现实一致且避免退料带来的顺序问题
          const progress = e.lines[recipe.id] ?? 0;
          if (progress === 0) {
            for (const res of Object.keys(recipe.inputs)) {
              e.inputs[res] = (e.inputs[res] ?? 0) - (recipe.inputs[res] ?? 0);
              bump(prod.windowConsumed, res, building.defId, recipe.inputs[res] ?? 0);
            }
          }
          const next = progress + MINUTES_PER_TICK;
          if (next >= recipe.cycleMinutes) {
            e.lines[recipe.id] = 0;
            for (const res of Object.keys(recipe.outputs)) {
              e.outputs[res] = (e.outputs[res] ?? 0) + (recipe.outputs[res] ?? 0);
              bump(prod.windowProduced, res, building.defId, recipe.outputs[res] ?? 0);
            }
          } else {
            e.lines[recipe.id] = next;
          }
        }
      }

      /* --- 4. 维护与统计 --- */
      for (const { entityId, spec } of list) {
        const e = ensureEntity(prod, entityId);
        for (const res of Object.keys(spec.maintenancePerDay).sort()) {
          const perDay = spec.maintenancePerDay[res] ?? 0;
          if (perDay <= 0) continue;
          // 欠账用整数「单位·分钟」累计，满一天结算一单位 —— 不引入浮点
          const debt = (e.maintenanceDebt[res] ?? 0) + perDay * MINUTES_PER_TICK;
          const due = Math.floor(debt / MINUTES_PER_DAY);
          e.maintenanceDebt[res] = debt - due * MINUTES_PER_DAY;
          if (due > 0) {
            const have = stock[res] ?? 0;
            const paid = due < have ? due : have;
            stock[res] = have - paid;
          }
        }
      }

      /* 统计窗口。
         只在满一天时结算的话，**开局第一天 HUD 的趋势全是 0** —— 生产明明在跑，
         玩家却看不到任何速率。因此累计满 MIN_WINDOW 分钟后就按比例外推，
         日界仍做一次精确结算并清零。
         外推用整数除法（floor），不引入浮点，日界处的值与直接累计一致。 */
      prod.windowMinutes += MINUTES_PER_TICK;
      if (prod.windowMinutes >= MINUTES_PER_DAY) {
        prod.flows = summariseFlows(prod, MINUTES_PER_DAY);
        prod.windowMinutes = 0;
        prod.windowProduced = {};
        prod.windowConsumed = {};
      } else if (prod.windowMinutes >= MIN_WINDOW_MINUTES) {
        prod.flows = summariseFlows(prod, prod.windowMinutes);
      }
    },
  };
}

function bump(bag: Record<string, Record<string, number>>, res: string, defId: string, amount: number): void {
  if (amount <= 0) return;
  const byDef = bag[res] ?? (bag[res] = {});
  byDef[defId] = (byDef[defId] ?? 0) + amount;
}

function sumOf(byDef: Record<string, number> | undefined): number {
  if (!byDef) return 0;
  let n = 0;
  for (const k of Object.keys(byDef).sort()) n += byDef[k] ?? 0;
  return n;
}

/** 外推所需的最小窗口。取一小时：短于此的样本噪声太大，长于此玩家等太久。 */
export const MIN_WINDOW_MINUTES = 60;

/** 按比例把窗口累计外推成「每日」速率。elapsed = MINUTES_PER_DAY 时即精确值。 */
function scaleToDay(amount: number, elapsed: number): number {
  return Math.floor((amount * MINUTES_PER_DAY) / elapsed);
}

function scaleMap(byDef: Record<string, number> | undefined, elapsed: number): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of Object.keys(byDef ?? {}).sort()) out[k] = scaleToDay(byDef![k] ?? 0, elapsed);
  return out;
}

function summariseFlows(prod: ProductionState, elapsed: number): Record<string, ResourceFlow> {
  const out: Record<string, ResourceFlow> = {};
  const names = new Set([...Object.keys(prod.windowProduced), ...Object.keys(prod.windowConsumed)]);
  for (const res of [...names].sort()) {
    out[res] = {
      producedPerDay: scaleToDay(sumOf(prod.windowProduced[res]), elapsed),
      consumedPerDay: scaleToDay(sumOf(prod.windowConsumed[res]), elapsed),
      byProducer: scaleMap(prod.windowProduced[res], elapsed),
      byConsumer: scaleMap(prod.windowConsumed[res], elapsed),
    };
  }
  return out;
}

/** 停机判定。顺序即优先级，先判公用设施再判物料。 */
function blockingReason(
  e: ProductionEntityState,
  spec: ProductionSpec,
  entityId: string,
  utility: UtilityProbe,
): ProductionBlock | null {
  if (spec.requiresPower) {
    if (!utility.powered(entityId)) return 'NO_POWER';
    if (utility.shed(entityId)) return 'POWER_SHED';
  }
  const needsWater = spec.recipes.some((r) => (r.requiresWater ?? 0) > 0);
  if (needsWater && !utility.hasWater(entityId)) return 'NO_WATER';
  if (spec.workerSlots > 0 && e.workers < spec.workerSlots) return 'NO_WORKERS';

  // 只要有任一条配方能跑就不算停机；全都跑不了才报物料原因。
  let anyRunnable = false;
  let outputFull = false;
  for (const recipe of spec.recipes) {
    if (canRun(e, recipe, spec)) { anyRunnable = true; break; }
    if (!hasRoom(e, recipe, spec)) outputFull = true;
  }
  if (anyRunnable) return null;
  return outputFull ? 'OUTPUT_FULL' : 'INPUT_MISSING';
}

function hasRoom(e: ProductionEntityState, recipe: ProductionRecipe, spec: ProductionSpec): boolean {
  const cap = bufferCap(spec);
  for (const res of Object.keys(recipe.outputs)) {
    if ((e.outputs[res] ?? 0) + (recipe.outputs[res] ?? 0) > cap) return false;
  }
  return true;
}

function canRun(e: ProductionEntityState, recipe: ProductionRecipe, spec: ProductionSpec): boolean {
  if (!hasRoom(e, recipe, spec)) return false;
  // 周期进行中不再检查输入：料已在起始时扣掉，中途不重复占用
  if ((e.lines[recipe.id] ?? 0) > 0) return true;
  for (const res of Object.keys(recipe.inputs)) {
    if ((e.inputs[res] ?? 0) < (recipe.inputs[res] ?? 0)) return false;
  }
  return true;
}

/* ===================== 投影（供 UI 与测试读取） ===================== */

/** 某实体的运转状况。 */
export function productionStatusOf(
  prod: ProductionState,
  entityId: string,
): { blocked: ProductionBlock | null; workers: number; inputs: Record<string, number>; outputs: Record<string, number> } {
  const e = prod.entities[entityId];
  if (!e) return { blocked: null, workers: 0, inputs: {}, outputs: {} };
  return { blocked: e.blocked, workers: e.workers, inputs: { ...e.inputs }, outputs: { ...e.outputs } };
}

/** 殖民地资源概览：库存、容量、日流量分解。 */
export function economySummary(
  prod: ProductionState,
  stock: Readonly<Record<string, number>>,
): {
  capacity: number;
  workforce: { available: number; assigned: number };
  resources: Array<{ id: string; amount: number; producedPerDay: number; consumedPerDay: number; netPerDay: number }>;
} {
  const names = new Set([...Object.keys(stock), ...Object.keys(prod.flows)]);
  const resources = [...names].sort().map((id) => {
    const f = prod.flows[id];
    const p = f?.producedPerDay ?? 0;
    const c = f?.consumedPerDay ?? 0;
    return { id, amount: stock[id] ?? 0, producedPerDay: p, consumedPerDay: c, netPerDay: p - c };
  });
  return {
    capacity: prod.storageCapacity,
    workforce: { available: prod.workforceAvailable, assigned: prod.workforceAssigned },
    resources,
  };
}
